import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Search, Loader2, Fuel, FilePlus2, Eye, Printer, RefreshCcw } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { jsPDF } from "jspdf";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

function StatusBadge({ status }) {
  const map = {
    blank: "bg-[#F50E02]/10 text-[#F50E02]",
    active: "bg-emerald-100 text-emerald-700",
    completed: "bg-emerald-100 text-emerald-700",
  };
  const cls = map[status] || "bg-[#525252]/10 text-[#525252]";
  const label = status === "blank" ? "Fuel Excess" : status;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${cls}`}
    >
      {label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function FuelAccess({ statusFilter, pageTitle, pageSubtitle } = {}) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const moduleKey = statusFilter === "blank" ? "blank_fuel_access" : "fuel_access";
  const canAdd = can(moduleKey, "add");
  const canDelete = can(moduleKey, "delete");
  const canEdit = can(moduleKey, "edit");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(statusFilter !== "blank");
  const [search, setSearch] = useState("");

  const today = todayISO();
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [qrInput, setQrInput] = useState("");
  const [printing, setPrinting] = useState(false);
  const qrRefs = useRef({}); // id -> canvas DOM node

  const [prefixes, setPrefixes] = useState([]);
  const [prefixesLoading, setPrefixesLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ prefix_id: "", quantity: "1" });
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [detailsTarget, setDetailsTarget] = useState(null);

  // Master-data id -> name maps for resolving columns
  const [masterMaps, setMasterMaps] = useState({
    wheel_counts: {},
    vehicle_brands: {},
    engine_stroke_types: {},
    cylinder_categories: {},
  });

  const loadMasters = async () => {
    try {
      const keys = ["wheel_counts", "vehicle_brands", "engine_stroke_types", "cylinder_categories"];
      const responses = await Promise.all(keys.map((k) => api.get(`/master/${k}`)));
      const maps = {};
      keys.forEach((k, idx) => {
        const m = {};
        for (const it of responses[idx].data || []) m[it.id] = it.name;
        maps[k] = m;
      });
      setMasterMaps(maps);
    } catch (e) {
      // non-fatal — table just shows "—" for these columns
    }
  };

  const loadList = async (fromOverride, toOverride) => {
    setLoading(true);
    try {
      let params;
      if (statusFilter === "blank") {
        params = { status: "blank" };
      } else {
        params = statusFilter ? { status: statusFilter } : { filled: "true" };
        const f = fromOverride !== undefined ? fromOverride : fromDate;
        const t = toOverride !== undefined ? toOverride : toDate;
        if (f) params.from = f;
        if (t) params.to = t;
      }
      const { data } = await api.get("/fuel-access", { params });
      setItems(data || []);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load fuel access"
      );
    } finally {
      setLoading(false);
    }
  };

  const loadPrefixes = async () => {
    setPrefixesLoading(true);
    try {
      const { data } = await api.get("/master/fuel_access_prefixes");
      setPrefixes(data || []);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load prefixes"
      );
    } finally {
      setPrefixesLoading(false);
    }
  };

  useEffect(() => {
    if (statusFilter !== "blank") {
      loadList();
    }
    loadMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const resetDates = () => {
    setFromDate(today);
    setToDate(today);
    loadList(today, today);
  };

  const handleQrSearch = () => {
    const val = qrInput.trim();
    if (!val) {
      toast.error("Please enter a QR code value to search.");
      return;
    }
    setSearch(val);
    loadList();
  };

  // Clear results when QR input is cleared
  const handleQrInputChange = (e) => {
    setQrInput(e.target.value);
    if (!e.target.value.trim()) {
      setSearch("");
      setItems([]);
    }
  };

  const handlePrintAll = async () => {
    if (filteredRows.length === 0) {
      toast.error("Nothing to print.");
      return;
    }
    setPrinting(true);
    try {
      // Wait one tick to ensure all hidden canvases are mounted.
      await new Promise((r) => setTimeout(r, 50));
      const doc = new jsPDF({
        unit: "mm",
        format: [75, 100],
        orientation: "portrait",
      });
      let firstPage = true;
      let printed = 0;
      for (const row of filteredRows) {
        const canvas = qrRefs.current[row.id];
        if (!canvas) continue;
        const dataUrl = canvas.toDataURL("image/png");
        const topText = (row.vehicle_number || "").trim() || row.temp_number || "";
        const bottomText = row.temp_number || "";

        if (!firstPage) doc.addPage([75, 100], "portrait");
        firstPage = false;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.text(topText, 37.5, 14, { align: "center" });

        const qrSize = 52; // mm
        doc.addImage(dataUrl, "PNG", (75 - qrSize) / 2, 22, qrSize, qrSize);

        doc.setFontSize(16);
        doc.text(bottomText, 37.5, 88, { align: "center" });
        printed += 1;
      }
      if (printed === 0) {
        toast.error("QR codes not ready yet. Please retry in a moment.");
        return;
      }
      const filename = `fuel-access-labels-${today}.pdf`;
      doc.save(filename);
      toast.success(`${printed} label${printed === 1 ? "" : "s"} downloaded (${filename})`);
    } catch (e) {
      toast.error("Could not generate PDF. Please try again.");
    } finally {
      setPrinting(false);
    }
  };

  const openAdd = () => {
    setForm({ prefix_id: "", quantity: "1" });
    setDialogOpen(true);
    if (prefixes.length === 0) loadPrefixes();
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        (it.temp_number || "").toLowerCase().includes(q) ||
        (it.qr_code || "").toLowerCase().includes(q) ||
        (it.vehicle_number || "").toLowerCase().includes(q) ||
        (it.prefix_name || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const quantityValid = () => {
    const n = Number(form.quantity);
    return Number.isInteger(n) && n >= 1 && n <= 500;
  };

  const formValid = () => !!form.prefix_id && quantityValid();

  const handleCreate = async () => {
    if (!formValid()) {
      toast.error("Select a prefix and enter a quantity between 1 and 500.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/fuel-access", {
        prefix_id: form.prefix_id,
        quantity: Number(form.quantity),
      });
      const n = Array.isArray(data) ? data.length : 0;
      setItems((prev) => [...(data || []), ...prev]);
      toast.success(`${n} fuel access record${n === 1 ? "" : "s"} created`);
      setDialogOpen(false);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Failed to create fuel access"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/fuel-access/${deleteTarget.id}`);
      setItems((prev) => prev.filter((it) => it.id !== deleteTarget.id));
      toast.success(`${deleteTarget.temp_number} deleted`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed"
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="fuel-access-page">
      <header className="bg-white border-b border-black/5 px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[#0A0A0A] tracking-tight">
            {pageTitle || "Fuel Access"}
          </h1>
          <p className="text-xs text-[#525252] mt-0.5">
            {pageSubtitle ||
              "Generate blank fuel access entries by prefix. Details are filled in later."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handlePrintAll}
            disabled={printing || loading || filteredRows.length === 0}
            variant="outline"
            className="border-black/10 h-10"
            data-testid="print-all-button"
          >
            {printing ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Printer className="h-4 w-4 mr-1.5" />
            )}
            Print All
          </Button>
          {canAdd && statusFilter !== "blank" ? (
            <Button
              onClick={openAdd}
              className="bg-[#F50E02] hover:bg-[#D80C02] text-white h-10 rounded-md"
              data-testid="add-fuel-access-button"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add Fuel Access
            </Button>
          ) : null}
        </div>
      </header>

      <div className="p-8">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          {statusFilter === "blank" ? (
            /* Vehicle Registration mode: QR code search only */
            <div className="flex flex-wrap items-end gap-3 w-full">
              <div className="flex-1 min-w-[240px] max-w-sm">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
                  QR Code Value
                </Label>
                <Input
                  placeholder="e.g. SREE-00012"
                  value={qrInput}
                  onChange={(e) => handleQrInputChange(e)}
                  onKeyDown={(e) => e.key === "Enter" && handleQrSearch()}
                  className="h-10 bg-white border-black/10"
                  data-testid="qr-search-input"
                />
              </div>
              <Button
                onClick={handleQrSearch}
                disabled={loading}
                className="bg-[#F50E02] hover:bg-[#D80C02] text-white h-10"
                data-testid="qr-search-button"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />}
                Search
              </Button>
            </div>
          ) : (
            /* Normal Fuel Access mode: date filters + text search */
            <>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
                    From Date
                  </Label>
                  <Input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="h-10 bg-white border-black/10"
                    data-testid="from-date"
                  />
                </div>
                <div>
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
                    To Date
                  </Label>
                  <Input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="h-10 bg-white border-black/10"
                    data-testid="to-date"
                  />
                </div>
                <Button
                  onClick={() => loadList()}
                  disabled={loading}
                  className="bg-[#F50E02] hover:bg-[#D80C02] text-white h-10"
                  data-testid="apply-dates"
                >
                  {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                  Apply
                </Button>
                <Button
                  variant="outline"
                  onClick={resetDates}
                  className="border-black/10 h-10"
                  data-testid="reset-dates"
                  title="Reset to today"
                >
                  <RefreshCcw className="h-4 w-4" />
                </Button>
              </div>
              <div className="relative w-full max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A3A3A3]" />
                <Input
                  placeholder="Search by QR code / temp number / vehicle number..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-10 bg-white border-black/10"
                  data-testid="fuel-search-input"
                />
              </div>
            </>
          )}
        </div>
        <div className="mb-4 flex items-center justify-end gap-4">
          <span className="text-xs text-[#525252]" data-testid="fuel-count-badge">
            {filteredRows.length} {filteredRows.length === 1 ? "record" : "records"}
          </span>
        </div>

        <div className="bg-white border border-black/5 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fuel-access-table">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-black/5 text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Sr.No.</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">QR Code</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Temp Number / Fuel Access QR</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Wheeler / Vehicle Brand</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Engine Stroke Type / Category of Cylinder</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252]">Vehicle Number / No of Cylinder</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#525252] text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-[#525252]">
                      <Loader2 className="h-5 w-5 animate-spin inline-block text-[#F50E02]" />
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-[#525252]" data-testid="fuel-empty-row">
                      <div className="inline-flex flex-col items-center gap-2">
                        <Fuel className="h-6 w-6 text-[#A3A3A3]" />
                        {search
                          ? <span>No records match &quot;{search}&quot;.</span>
                            : statusFilter === "blank"
                            ? <span>Enter a QR code value above and click Search.</span>
                            : <span>No fuel access records yet. Click &quot;Add Fuel Access&quot; to generate some.</span>
                        }
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row, idx) => {
                    const dash = <span className="text-[#A3A3A3]">—</span>;
                    const mv = (mapKey, idKey) =>
                      row[idKey] && masterMaps[mapKey]?.[row[idKey]]
                        ? masterMaps[mapKey][row[idKey]]
                        : dash;
                    const StackedCell = ({ items }) => (
                      <div className="space-y-2">
                        {items.map(({ label, value }) => (
                          <div key={label}>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#A3A3A3]">
                              {label}
                            </div>
                            <div className="text-sm text-[#0A0A0A]">{value}</div>
                          </div>
                        ))}
                      </div>
                    );
                    return (
                      <tr
                        key={row.id}
                        className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA]/60 align-top"
                        data-testid={`fuel-row-${row.id}`}
                      >
                        <td className="px-4 py-4 text-[#525252]">{idx + 1}</td>
                        <td className="px-4 py-4">
                          <div
                            className="p-1.5 bg-white rounded-md border border-black/5 inline-block"
                            title={row.id}
                          >
                            <QRCodeCanvas
                              value={row.id}
                              size={84}
                              level="M"
                              includeMargin={false}
                              bgColor="#FFFFFF"
                              fgColor="#0A0A0A"
                              ref={(el) => {
                                if (el) qrRefs.current[row.id] = el;
                                else delete qrRefs.current[row.id];
                              }}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <StackedCell
                            items={[
                              { label: "Temp Number", value: row.temp_number },
                              { label: "Fuel Access QR Code", value: row.qr_code || row.temp_number },
                            ]}
                          />
                        </td>
                        <td className="px-4 py-4">
                          <StackedCell
                            items={[
                              { label: "Wheeler", value: mv("wheel_counts", "wheel_count_id") },
                              { label: "Vehicle Brand", value: mv("vehicle_brands", "vehicle_brand_id") },
                            ]}
                          />
                        </td>
                        <td className="px-4 py-4">
                          <StackedCell
                            items={[
                              { label: "Engine Stroke Type", value: mv("engine_stroke_types", "engine_stroke_type_id") },
                              { label: "Category of Cylinder", value: mv("cylinder_categories", "cylinder_category_id") },
                            ]}
                          />
                        </td>
                        <td className="px-4 py-4">
                          <StackedCell
                            items={[
                              { label: "Vehicle Number", value: row.vehicle_number || dash },
                              { label: "No of Cylinder", value: row.no_of_cylinder || dash },
                            ]}
                          />
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => navigate(`/fuel-access/${row.id}`)}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#525252] hover:text-[#F50E02] hover:bg-[#F50E02]/10 transition-colors"
                              aria-label="View"
                              title="View"
                              data-testid={`fuel-view-${row.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {row.status === "blank" && canAdd ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/fuel-access/${row.id}/details`)}
                                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#525252] hover:text-[#F50E02] hover:bg-[#F50E02]/10 transition-colors"
                                aria-label="Add details"
                                title="Add details"
                                data-testid={`fuel-details-${row.id}`}
                              >
                                <FilePlus2 className="h-4 w-4" />
                              </button>
                            ) : row.status !== "blank" && canEdit ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/fuel-access/${row.id}/details`)}
                                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#525252] hover:text-[#F50E02] hover:bg-[#F50E02]/10 transition-colors"
                                aria-label="Edit details"
                                title="Edit details"
                                data-testid={`fuel-details-${row.id}`}
                              >
                                <FilePlus2 className="h-4 w-4" />
                              </button>
                            ) : null}
                            {canDelete ? (
                              <button
                                type="button"
                                onClick={() => setDeleteTarget(row)}
                                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-[#525252] hover:text-[#F50E02] hover:bg-[#F50E02]/10 transition-colors"
                                aria-label="Delete"
                                data-testid={`fuel-delete-${row.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Fuel Access</DialogTitle>
            <DialogDescription>
              Pick a prefix and how many blank entries to generate. Each one gets a unique
              auto-incremented temp number like <b>SREE-00016</b>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
                Prefix
              </Label>
              <Select
                value={form.prefix_id}
                onValueChange={(v) => setForm((p) => ({ ...p, prefix_id: v }))}
                disabled={prefixesLoading}
              >
                <SelectTrigger
                  className="h-10 bg-white border-black/10"
                  data-testid="prefix-select"
                >
                  <SelectValue
                    placeholder={
                      prefixesLoading ? "Loading prefixes..." : "Choose a prefix"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {prefixes.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-[#525252]">
                      No prefixes available. Create one in Master Data → Fuel Access Prefix.
                    </div>
                  ) : (
                    prefixes.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block">
                Quantity
              </Label>
              <Input
                type="number"
                min={1}
                max={500}
                value={form.quantity}
                onChange={(e) => setForm((p) => ({ ...p, quantity: e.target.value }))}
                placeholder="Enter quantity"
                className="h-10 bg-white border-black/10"
                data-testid="quantity-input"
              />
              <p className="text-xs text-[#A3A3A3] mt-1">
                Generate between 1 and 500 blank fuel access entries at a time.
              </p>
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-black/10"
              data-testid="fuel-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={saving || !formValid()}
              className="bg-[#F50E02] hover:bg-[#D80C02] text-white"
              data-testid="fuel-save"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete fuel access?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <b>{deleteTarget?.temp_number}</b>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="fuel-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
              className="bg-[#F50E02] hover:bg-[#D80C02] text-white"
              data-testid="fuel-delete-confirm"
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
